// Per-Type Catalog , item-kind types (Slice 1).
// docs/expansion-roadmap-2026/38-per-type-catalog-branch-sharing-and-inventory.md (Part 1)
//
// The catalog spine (`menu_items`) is polymorphic: one table, a `kind`
// discriminator, and a sparse `type_profile` jsonb whose SHAPE is decided by the
// kind. These types + the per-kind Zod schemas are the first-class, validated
// structure each business type "deserves" , WITHOUT forking storage into three
// tables (a mixed menu, e.g. a restaurant-with-a-bar, holds several kinds at once).
//
// Client-safe on purpose: the menu-item editor (a client component) imports these.
// No server-only imports here.

export const ITEM_KIND_IDS = ['dish', 'package', 'drink', 'service', 'combo'] as const;
export type ItemKind = (typeof ITEM_KIND_IDS)[number];

export const PRICING_MODE_IDS = ['flat', 'per_person', 'per_pour', 'per_unit_weight', 'per_plate'] as const;
export type PricingMode = (typeof PRICING_MODE_IDS)[number];

export const DEFAULT_ITEM_KIND: ItemKind = 'dish';

// ── Per-kind profile shapes ──────────────────────────────────────────────────

/** `dish` (standard) , today's item. No extra structure: profile is `{}`. */
export type DishProfile = Record<string, never>;

/** A catering package price tier (Silver/Gold/Platinum), each a per-head figure. */
export interface PackageTier {
  id: string;
  name: string;
  price_per_person: number;
}

/**
 * `package` (catering). The item's headline `price` is the base per-person
 * figure; tiers add per-head alternatives. "Choose N from M" selections are NOT
 * stored here , they reuse `menu_item_option_groups` (min_select/max_select).
 */
export interface PackageProfile {
  min_headcount?: number;
  lead_time_days?: number;
  deposit_pct?: number;
  tiers: PackageTier[];
  serves_note?: string;
}

/**
 * `drink` (bar). Pour SIZES live as option-group options (single/double/bottle),
 * each carrying its own price_delta + volume , not in this profile. The profile
 * holds only the drink's scalar attributes.
 */
export interface DrinkProfile {
  abv?: number;
  age_restricted?: boolean;
  happy_hour_price_delta?: number;
  default_pour_id?: string;
}

/** `service` (add-ons: staff hours, equipment, corkage) , a non-food line item. */
export interface ServiceProfile {
  unit_label?: string;
}

/** A single selectable slot inside a combo (e.g. "Main", "Side", "Drink"). */
export interface ComboComponent {
  slot: string;
  eligible_item_ids: string[];
  default_id?: string | null;
  included_qty: number;
}

/** `combo` (fast food). A bundled meal deal combining multiple items at a set price. */
export interface ComboProfile {
  components: ComboComponent[];
}

/** Discriminated map from kind → its profile shape. */
export interface ItemProfileByKind {
  dish: DishProfile;
  package: PackageProfile;
  drink: DrinkProfile;
  service: ServiceProfile;
  combo: ComboProfile;
}

/** Any valid profile , the JSON stored in `menu_items.type_profile`. */
export type ItemTypeProfile = ItemProfileByKind[ItemKind];
